import React from 'react';
import {observer} from 'mobx-react';
import debounce from 'lodash/debounce';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {CameraZoomControl} from '@techsee/techsee-ui-common/lib/camera-zoom-control';
import Translate from '../../../../../components/translate/Translate';
import classNames from 'classnames';
import {TsLoader} from '../../../../../components/ts-loader/ts-loader';
import {SessionSummary} from '../../../../../components/session-summary/SessionSummary';
import {TsIdleMode} from '../../../../../components/ts-idle-mode/TsIdleMode';
import {TsFloatingToolBarContainer} from '../../../../../components/ts-floating-toolbar/TsFloatingToolBarContainer';
import {WifiAnalyzer} from '../../../../../components/wifi-analyzer/component';
import {WifiAnalyzerScan} from '../../../../../components/connectivity-guru/wifi-analyzer-scan/component';
import {LeftBarComponent} from '../../../../../layouts/main-layout/left-bar-base/LeftBarComponent';
import {SelfServiceComponent} from '../../../self-service/component';
import {MainCameraPreApproval} from '../main-camera-pre-approval/MainCameraPreApproval';
import {MainTermsAndConditions} from '../MainTermsAndConditions';
import {MainPhotoSelection} from '../main-photo-selection/MainPhotoSelection';
import {OneClickGuidance} from '../one-click-guidance/OneClickGuidance';
import {MessageAlert} from '@techsee/techsee-ui-common/lib/message-alert';
import {TsIdleLoader} from '../../../../../components/ts-idle-loader/ts-idle-loader';
import {TsLiveVideoStretched} from '../../../../../components/ts-live-video/TsLiveVideoStretched';
import {TsWidgets} from '../../../../../components/ts-widgets/ts-widgets.component';
import {RightBar} from '../../../../../components/right-bar';
import Copilot from '../../../../../components/copilot';
import {SearchResultsDialog} from '../../../../../modals/search-result-dialog/SearchResultDialog.component';
import {ScanResultModal} from '../../../../../modals/scan-result-modal/ScanResultModal.component';
import {SwitchModeModal} from '../../../../../modals/switch-mode-modal/SwitchModeModal.component';
import {CustomerHistoryModal} from '../../../../../modals/customer-history-modal/CustomerHistoryModal.component';
import {requireImage} from '../../../../../utils/utils';
import TsMuteButton from '../../../../../components/dashboard/ts-mute-button/ts-mute-button.component';

import './styles.scss';

export interface IMainViewProps {
    main: any; // MainController
}

@observer
export class MainView extends TechseeBaseComponent<IMainViewProps> {
    private _handleZoomChange = debounce((level: number) => {
        const {main} = this.props;
        main?.dashboardState?.cameraZoomController?.requestZoom(main?.chatApi, level);
    }, 50);

    componentWillUnmount(): void {
        this._handleZoomChange.cancel();
        const {main} = this.props;
        main?.dashboardState?.cameraZoomController?.clearPendingZoom();
    }

    componentDidMount(): void {
        const {main} = this.props;
        const dashboardState = main?.dashboardState;
        if (dashboardState?.useNewInterface && dashboardState?.dashboardControllerLoaded) {
            main?.onViewReady?.();
        }
    }

    renderInternal(): JSX.Element {
        const {main} = this.props;
        const dashboardState = main?.dashboardState;

        if (!dashboardState?.useNewInterface || !dashboardState?.dashboardControllerLoaded) {
            return <React.Fragment />;
        }

        return (
            <>
                {/* Loaders and Session Summary */}
                {dashboardState.displayLoader && (
                    <TsLoader
                        useNewInterface={dashboardState.useNewInterface}
                        controller={main.tsLoaderController}
                        requireImage={requireImage}></TsLoader>
                )}

                {dashboardState.allowSessionSummary && (
                    <div className='ts-session-summary'>
                        <SessionSummary controller={main.sessionSummaryController} />
                    </div>
                )}

                {/* Reconnecting Loader */}
                {dashboardState.displayLoaderReconnecting &&
                    !(dashboardState.isDesktopSharingSession && !dashboardState.clientDisconnected) &&
                    dashboardState.videoModeState.videoState.focus && (
                        <div className='loader-connect'>
                            {!main.idleMode ? (
                                <TsLoader
                                    useNewInterface={dashboardState.useNewInterface}
                                    controller={main.tsLoaderController}
                                    requireImage={requireImage}
                                />
                            ) : (
                                <div className='idle-client'>
                                    <div className='ts-idle-mode'>
                                        <TsIdleMode
                                            notification={main.idleClient.idleNotification}
                                            inviteMethod={main.idleClient.inviteMethod}
                                            buttonText={main.idleClient.sendSMSButtonText}
                                            onSendSmsClick={main.onIdleClient}
                                            isSmsSent={dashboardState.notificationSmsInProgress}
                                            connectingText={main.idleClient.connectingText}
                                            dir={main.dir}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                {/* Floating Toolbar */}
                {main.isToggleEnabled_SessionToolbar() && main.tsFloatingToolbarController && (
                    <TsFloatingToolBarContainer controller={main.tsFloatingToolbarController} />
                )}

                {/* Wifi Analyzer and Modals */}
                {main.wifiAnalyzerMode && main.wifiAnalyzerController && (
                    <WifiAnalyzer ctrl={main.wifiAnalyzerController} mode={main.wifiAnalyzerMode.Session} />
                )}
                {main.searchResultsDialogController && (
                    <SearchResultsDialog ctrl={main.searchResultsDialogController} />
                )}
                {main.scanResultModalController && <ScanResultModal ctrl={main.scanResultModalController} />}
                {main.switchModeModalController && <SwitchModeModal ctrl={main.switchModeModalController} />}
                {main.customerHistoryModalController && (
                    <CustomerHistoryModal ctrl={main.customerHistoryModalController} />
                )}

                {/* Main Container */}
                <div className='dashboard-main-container ts-horizontal-layout dashboard-main-bright'>
                    {/* Left Bar */}
                    {!dashboardState.libraryTaggingMode && main.leftBarController && (
                        <div className='left-bar'>
                            <LeftBarComponent controller={main.leftBarController} />
                        </div>
                    )}

                    {/* Self Service */}
                    {main.selfServiceController && main.displayNewSelfServiceSummary && (
                        <SelfServiceComponent ctrl={main.selfServiceController} />
                    )}

                    {/* Camera Pre-Approval */}
                    {dashboardState.clientRoomStorage.isOnPreCameraApprovalScreen &&
                        (!main.idleMode ||
                            (!dashboardState.displayLoaderReconnecting && !dashboardState.endedTheMeeting)) && (
                            <div className='dashboard-main-content-container ts-flex-expand ts-vertical-layout camera-pre-approval-container new-header-and-footer'>
                                <MainCameraPreApproval
                                    dir={main.dir}
                                    getPreCameraApprovalImage={main.getPreCameraApprovalImage}
                                />
                            </div>
                        )}

                    {/* Terms and Conditions */}
                    {dashboardState.clientRoomStorage.isReviewingTOS && (
                        <div className='dashboard-main-content-container ts-flex-expand ts-vertical-layout new-header-and-footer'>
                            <MainTermsAndConditions
                                visible={main.chatApi.isReviewingTOS}
                                dir={main.dir}
                                isAndroidOrIphone={main.isAndroidOrIphone}
                                isIE11={main.isIE11}
                                useNewInterface={main.tsInterfaceHelper.useNewInterface}
                                rejectedTermsAndConditions={dashboardState.rejectedTermsAndConditions}
                            />
                        </div>
                    )}

                    {/* Main Content */}
                    {dashboardState.displayMainContent && !dashboardState.rejectedTermsAndConditions && (
                        <div className='dashboard-main-content-container ts-flex-expand text-center ts-vertical-layout new-header-and-footer'>
                            {/* Wifi Analyzer Scan */}
                            {!dashboardState.hideWifiScan && main.wifiAnalyzerMode && (
                                <WifiAnalyzerScan
                                    ctrl={main.wifiAnalyzerScanController}
                                    mode={main.wifiAnalyzerMode.Session}
                                />
                            )}

                            {/* Photo Selection Guidance */}
                            {dashboardState.areBothSidesConnected && dashboardState.showPhotoSelectionDialog && (
                                <div className='guidance-container-view'>
                                    <MainPhotoSelection
                                        dashboardState={dashboardState}
                                        isAndroidOrIphone={dashboardState.isAndroidOrIphone}
                                    />
                                </div>
                            )}

                            {/* Message Alert */}
                            {(main.mode === main.MeetingMode?.oneClick || main.mode === main.MeetingMode?.images) &&
                                dashboardState.messageAlert?.display && (
                                    <MessageAlert
                                        className='use-common-styles message-alert-audio'
                                        color={dashboardState.messageAlert.color}
                                        label={dashboardState.messageAlert.label}
                                    />
                                )}

                            {/* One Click Guidance */}
                            {dashboardState.isImagesOrOneClickMode &&
                                (dashboardState.displayOneClickGuidnace ||
                                    (dashboardState.returnToOneClickGuidance &&
                                        dashboardState.displayLoaderReconnecting)) && (
                                    <OneClickGuidance
                                        dashboardState={dashboardState}
                                        isAndroidOrIphone={dashboardState.isAndroidOrIphone}
                                        micFabSlot={
                                            dashboardState.displayAudioButton &&
                                            dashboardState.useNewInterface &&
                                            dashboardState.areBothSidesConnected &&
                                            !dashboardState.currentResource?.url && (
                                                <div className='dashboard-photo-mode-mic'>
                                                    <TsMuteButton
                                                        useNewInterface={dashboardState.useNewInterface}
                                                        isMicrophoneMuted={
                                                            dashboardState.videoModeState.isMicrophoneMuted
                                                        }
                                                        isSupportAudio={dashboardState.videoModeState.isSupportAudio}
                                                        toggleMute={main.videoModeController.toggleMute}
                                                    />
                                                    <span className='dashboard-photo-mode-mic-hint'>
                                                        <Translate text='MAIN.VIEW.PHOTO_MODE_MIC_HINT' />
                                                    </span>
                                                </div>
                                            )
                                        }
                                    />
                                )}

                            {/*/!* Co-browsing Container *!/*/}
                            {/*{main.inCobrowsingMode() && (*/}
                            {/*    <div className="cobrowsing-container">*/}
                            {/*        {(main.tsCobrowsing.showLoader || main.tsCobrowsing.otherSideLoading) && (*/}
                            {/*            <div*/}
                            {/*                className="use-common-styles centered-spinner"*/}
                            {/*                style={main.tsCobrowsing.clientDimensions}>*/}
                            {/*                <Spinner*/}
                            {/*                    className="centered-spinner"*/}
                            {/*                    spinnerType={dashboard.spinnerOptions.type}*/}
                            {/*                    backgroundColorName={dashboard.spinnerOptions.backgroundColor}*/}
                            {/*                    colorName={dashboard.spinnerOptions.color}*/}
                            {/*                    sizeName={dashboard.spinnerOptions.size}*/}
                            {/*                />*/}
                            {/*            </div>*/}
                            {/*        )}*/}

                            {/*        {!main.tsCobrowsing.isInControl && (*/}
                            {/*            <div*/}
                            {/*                className="co-browsing-glass-window-container"*/}
                            {/*                style={main.tsCobrowsing.clientDimensions}>*/}
                            {/*                <div className="co-browsing-glass-window" />*/}
                            {/*                <div className="co-browsing-not-in-control">*/}
                            {/*                    <Translate text="MAIN.VIEW.MODE.CO_BROWSING_NOT_IN_CONTROL" />*/}
                            {/*                </div>*/}
                            {/*            </div>*/}
                            {/*        )}*/}

                            {/*        {main.tsCobrowsing.isPaused && (*/}
                            {/*            <div className="sessionInPause" style={main.tsCobrowsing.clientDimensions}>*/}
                            {/*                <img src={requireImage('pause_mode.png')} alt="pause mode" />*/}
                            {/*            </div>*/}
                            {/*        )}*/}

                            {/*        {main.displayCobrowsingIframe() && !dashboardState.endedTheMeeting && (*/}
                            {/*            <iframe*/}
                            {/*                id="coBrowsingFollowerFrame"*/}
                            {/*                style={main.tsCobrowsing.clientDimensions}*/}
                            {/*            />*/}
                            {/*        )}*/}
                            {/*    </div>*/}
                            {/*)}*/}

                            {/* Mode Labels */}
                            {!!main.video.MODES[main.video.mode] &&
                                !(
                                    dashboardState.isToggleEnabled_ScanCaptureDesign &&
                                    main.video.mode === main.video.MODES.SCANNING
                                ) &&
                                main.showModeLabel &&
                                dashboardState.videoModeState.videoState.focus &&
                                !main.isPaused &&
                                !(
                                    dashboardState.isToggleEnabled_RoundedFloatingVideo &&
                                    dashboardState.isToggleEnabled_StretchedVideoMode
                                ) && (
                                    <div className='alert alert-info alert-floating-bright ts-fade-out'>
                                        <h4>
                                            <Translate text={`MAIN.VIEW.MODE.${main.video.mode}`} />
                                        </h4>
                                    </div>
                                )}

                            {/* Scanning Label */}
                            {dashboardState.isToggleEnabled_ScanCaptureDesign &&
                                main.video.mode === main.video.MODES.SCANNING &&
                                dashboardState.videoModeState.videoState.focus &&
                                !main.isPaused &&
                                !(
                                    dashboardState.isToggleEnabled_RoundedFloatingVideo &&
                                    dashboardState.isToggleEnabled_StretchedVideoMode
                                ) && (
                                    <div className='scanning-label'>
                                        <Translate text='MAIN.VIEW.MODE.SCANNING' />
                                        <div className='scanning-label-close' onClick={() => main.cancelScanning()} />
                                    </div>
                                )}

                            {/* Live Video */}
                            <div
                                className={classNames('ts-live-video-container', {
                                    'not-connected': !dashboardState.areBothSidesConnected,
                                    'desktop-sharing-pause': dashboardState.isDesktopSharing && main.isPaused
                                })}>
                                <TsLiveVideoStretched
                                    controller={main.tsVideoSubscriberController}
                                    dashboardState={dashboardState}
                                    isClientPointerUnavailable={main.isClientPointerUnavailable}
                                    messageAlert={dashboardState.messageAlert}
                                    displayNewScanMode={main.displayNewScanMode()}
                                    agentSnapshotReminder={main.agentSnapshotReminder}
                                    snapshotReminderController={main.snapshotReminderController}
                                    isAndroid={main.isAndroid()}
                                    isToggleEnabledIdleModeUsability={true}
                                    locale={main.locale}
                                    overlayControl={
                                        dashboardState.cameraZoomController.displayCameraZoomControl &&
                                        !dashboardState.isOnEditMode ? (
                                            <CameraZoomControl
                                                min={dashboardState.clientRoomStorage.zoomCapability?.min ?? 1}
                                                max={dashboardState.clientRoomStorage.zoomCapability?.max ?? 5}
                                                step={dashboardState.clientRoomStorage.zoomCapability?.step ?? 0.1}
                                                value={dashboardState.clientRoomStorage.zoomLevel}
                                                disabled={
                                                    dashboardState.cameraZoomController.cameraZoomDisabled ||
                                                    dashboardState.cameraZoomController.isZoomPending
                                                }
                                                onChange={this._handleZoomChange}
                                                onReset={() => {
                                                    this._handleZoomChange.cancel();
                                                    dashboardState.cameraZoomController.requestZoom(main?.chatApi, 1);
                                                }}
                                            />
                                        ) : undefined
                                    }
                                />
                            </div>

                            {/* Widgets */}
                            {(main.showVideoWidget || dashboardState.showImageWidget) &&
                                !dashboardState.hideWidgets &&
                                dashboardState.hideWifiScan &&
                                !(
                                    dashboardState.returnToOneClickGuidance && dashboardState.displayLoaderReconnecting
                                ) &&
                                !dashboardState.endedTheMeeting && (
                                    <div
                                        className={classNames('dashboard-main-widget-container', {
                                            'tablet-view-main-widget-container': main.isTablet
                                        })}>
                                        <TsWidgets controller={main.tsWidgetsController} />
                                    </div>
                                )}

                            {/* Offline Editor Message */}
                            {main.libraryTaggingMode && !dashboardState.videoModeState.preloadedImage.resource && (
                                <div className='dashboard-offline-editor-message'>
                                    <Translate text='MAIN.VIEW.SELECT_LIBRARY_IMAGE' />
                                </div>
                            )}

                            {/* Main Content Bar */}
                            {!main.observe && !dashboardState.rejectedTermsAndConditions && (
                                <div className='dashboard-main-content-bar main-text-color'>
                                    <div className='dashboard-main-content-bar-inner' />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Right Bar */}
                    {!main.hideRightSidebar &&
                        !dashboardState.isInInitiationPhase &&
                        (!main.showWizard ||
                            (dashboardState.connectionStatus === main.CONNECTION_STATUS?.CONNECTED &&
                                main.chatApi?.client?.mode === main.MeetingMode?.images)) && (
                            <div
                                className='dashboard-right-sidebar new-header'
                                dir={main.dir}
                                onMouseLeave={() => main.closeRightBarIfNotFocused()}>
                                <RightBar
                                    rightBarController={main.rightBarController}
                                    dashboardState={dashboardState}
                                />
                            </div>
                        )}

                    {/* Audio Subscribers */}
                    <div className='audio-subscriber-container-client' />
                    <div className='audio-subscriber-container-observer' />
                    <div className='audio-subscriber-container-agent' />

                    {/* Copilot */}
                    {main.isCopilotEnabled && <Copilot init={main.initCopilot} username={main.currentUser._id} />}
                </div>
            </>
        );
    }
}
